'use client';

import React from 'react';
import type { ElementType, ReactNode, Ref } from 'react';
import { HelpCircle } from '@untitledui/icons';
import type { LabelProps as AriaLabelProps, TextProps as AriaTextProps } from 'react-aria-components';
import { Label as AriaLabel, Text as AriaText } from 'react-aria-components';
import { Tooltip } from '../tooltip';
import { cx } from '../../../utils/cx';

const SEMANTIC_NAME = 'RomaField';
const SEMANTIC_NAME_LABEL = 'RomaLabel';
const SEMANTIC_NAME_HINT_TEXT = 'RomaHintText';

type FieldProps<T extends ElementType = 'div'> = {
    as?: T;
    children: React.ComponentPropsWithoutRef<T>['children'];
    className?: React.ComponentPropsWithoutRef<T>['className'];
} & Omit<React.ComponentPropsWithoutRef<T>, 'as' | 'children' | 'className'>;

export interface LabelProps extends AriaLabelProps {
    children: ReactNode;
    size?: 'sm' | 'md' | 'lg';
    isRequired?: boolean;
    tooltip?: string;
    tooltipDescription?: string;
}

export interface HintTextProps extends AriaTextProps {
    /** Indicates that the hint text is an error message. */
    isInvalid?: boolean;
    children: ReactNode;
}

const labelSizes = {
    sm: 'text-sm',
    md: 'text-md',
    lg: 'text-md',
};

const Label = ({ size = 'sm', isRequired, tooltip, tooltipDescription, className, ...props }: LabelProps) => {
    return (
        <AriaLabel
            data-label="true"
            {...props}
            className={cx(
                SEMANTIC_NAME_LABEL,
                'flex cursor-default items-center gap-0.5 font-medium text-secondary',
                labelSizes[size],
                className,
            )}
        >
            {props.children}

            <span
                className={cx(
                    'hidden text-brand-tertiary',
                    isRequired && 'block',
                    typeof isRequired === 'undefined' && 'group-required:block',
                )}
            >
                *
            </span>

            {tooltip && (
                <Tooltip title={tooltip} description={tooltipDescription} placement="top">
                    <Tooltip.Trigger
                        isDisabled={false}
                        className="cursor-pointer text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover"
                    >
                        <HelpCircle className="size-4" />
                    </Tooltip.Trigger>
                </Tooltip>
            )}
        </AriaLabel>
    );
};

Label.displayName = SEMANTIC_NAME_LABEL;

const HintText = ({ isInvalid, className, ...props }: HintTextProps) => {
    return (
        <AriaText
            {...props}
            slot={isInvalid ? 'errorMessage' : 'description'}
            className={cx(
                SEMANTIC_NAME_HINT_TEXT,
                'text-sm text-tertiary',
                isInvalid && 'text-error-primary',
                'group-invalid:text-error-primary',
                className,
            )}
        />
    );
};

HintText.displayName = SEMANTIC_NAME_HINT_TEXT;

const FieldRootComponent = <T extends ElementType = 'div'>(
    { as, className, ...props }: FieldProps<T>,
    ref: Ref<HTMLElement>,
) => {
    const Component = (as || 'div') as React.ElementType;
    const baseClassName = `${SEMANTIC_NAME} group flex h-max w-full flex-col items-start justify-start gap-1.5`;
    const fieldClassName =
        typeof className === 'function'
            ? (state: unknown) => cx(baseClassName, className(state))
            : cx(baseClassName, className);

    return <Component {...(props as Record<string, unknown>)} ref={ref} className={fieldClassName} />;
};

const FieldRoot = Object.assign(
    React.forwardRef(FieldRootComponent) as <T extends ElementType = 'div'>(
        props: FieldProps<T>,
    ) => React.ReactElement | null,
    { displayName: SEMANTIC_NAME },
);

export const Field = Object.assign(FieldRoot, {
    Label,
    HintText,
});
